import moment from "moment";
import momentTimezone from "moment-timezone";

export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/** Legacy booking date-times are UTC even though their strings have no offset. */
export const parseBookingTimestamp = (value: unknown): Date | null => {
  if (typeof value !== 'string' || !value.trim()) return null;
  const parsed = /T.*(?:Z|[+-]\d{2}:\d{2})$/.test(value)
    ? moment.parseZone(value, moment.ISO_8601, true)
    : moment.utc(value, 'MM/DD/YYYY hh:mm A', true);
  return parsed.isValid() ? parsed.toDate() : null;
};

const formatMissingBookingTime = (value: unknown): string => {
  if (typeof value !== 'string') return '—';
  const date = moment.utc(value, ['MM/DD/YYYY', 'YYYY-MM-DD'], true);
  return date.isValid() ? `${date.format('MM/DD/YYYY')} (time unavailable)` : '—';
};

export const formatBookingTimestamp = (value: unknown, timezone: string): string => {
  const date = parseBookingTimestamp(value);
  if (!date) return formatMissingBookingTime(value);
  return momentTimezone(date).tz(timezone).locale('en').format('MM/DD/YYYY, hh:mm A');
};

export const formatBookingTimestampForCsv = (value: unknown): string => {
  const date = parseBookingTimestamp(value);
  if (!date) return formatMissingBookingTime(value);
  return date.toISOString();
};

const UNTIL_CANCELLED = "Until Cancelled";
const YEAR_WE_WILL_ALL_BE_DEAD_BY = 2200;

export const formatDate = (date: string) => {
  if (date !== '') {
    const [ year, month, day ] = date.split('-');
    return [ month, day, year ].join('/');
  } else {
    return date;
  }
}

export const formatDateYYMMDD = (date: string) => {
  if (date !== '') {
    const [ month, day, year ] = date.split('/');
    return [ year, month, day ].join('-');
  } else {
    return date;
  }
}

export const formatDateMMDDYYYY = (date: moment.MomentInput) => {
  return moment(date).format('MM/DD/YYYY hh:mm A')
}

export const formatDateMMDDyyyy = (date: moment.MomentInput) => {
  return moment(date).format('MM/DD/YYYY')
}

export const formatDateMDyyyy = (date: moment.MomentInput) => {
  return moment(date).format('M/D/yyyy');
}

/**
 * A `MM/DD/YYYY` day read from a URL query parameter, or `undefined` when it is
 * missing or not a real day in exactly that shape, so a mangled link falls back
 * to the page's default date window instead of filtering on a bad date.
 */
export const parseQueryParamDay = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const day = moment(value, 'MM/DD/YYYY', true);
  return day.isValid() ? day.format('MM/DD/YYYY') : undefined;
};

export const calculateNumberOfDays = (startDate: moment.MomentInput, endDate: moment.MomentInput) => {
  const duration = moment(endDate).diff(startDate, 'days') + 1;
  return duration
}


export function calculateProratedAmount(startDate: moment.MomentInput, totalAmount: number, duration: number) {
  const daysInMonth = moment(startDate).daysInMonth();

  return (totalAmount / daysInMonth) * duration;
}

export const formatEndDateDisplay = (endDate: string, format?: string, addDash?: boolean) => {
  if (moment(endDate).year() === YEAR_WE_WILL_ALL_BE_DEAD_BY) {
    return UNTIL_CANCELLED
  } else {
    return `${addDash ? ' - ' : ''}${moment(endDate).format(format)}`
  }
}

export const getMsFromMinutes = (minutes: number) => minutes * 60000;
export const getMsFromHours = (hours: number) => getMsFromMinutes(hours * 60);

export const formatDurationInDHM: (date: string) => string = (date: string) => {
  let timeInYard = '-';

  const checkInMoment = moment(date);

  if (!checkInMoment.isValid()) {
    return timeInYard;
  }

  const durationInMs = moment().diff(checkInMoment);

  if (!Number.isFinite(durationInMs) || durationInMs < 0) {
    return timeInYard;
  }
  const duration = moment.duration(durationInMs);
  const totalDays = Math.floor(duration.asDays());
  const hours = duration.hours();
  const minutes = duration.minutes();

  if (durationInMs < getMsFromMinutes(1)) {
    // if check in is less than 1 minute ago
    timeInYard = 'Just Now';
  } else if (durationInMs < getMsFromHours(1)) {
    // if check in is less than 1 hour ago
    timeInYard = `${minutes}m`;
  } else if (durationInMs < getMsFromHours(24)) {
    // if check in is less than 1 day ago
    timeInYard = `${hours}h ${minutes}m`;
  } else {
    // if check in is more than 1 day ago
    timeInYard = `${totalDays}d ${hours}h`;
  }

  return timeInYard;
}
